import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import { FormattedMessage, useIntl } from 'react-intl';
import { useCookies } from 'react-cookie';
import { parse } from 'querystring';
import { Page } from '../../components/page';
import { PaymentInformationSection } from '../../components';
import { LoadingProgress } from '../../layout';
import {
  fromAccounts,
  fromBillingInformation,
  fromCoupon,
  fromOrder,
  fromPaymentMethod,
  fromSubscription,
  useAppDispatch,
} from '../../store';
import { QueryDictionary, useNavigate, useQuery } from '../../routes';
import {
  useGetQuotaAndCycleFromPathParams,
  useGetTrackingCookies,
  usePlanCartSnapshot,
} from '../../hook';
import { TermAndConditionCheck } from '../checkout/order-confirmation-section/TermAndConditionCheck';
import {
  paymentStatus as paymentStatusRoute,
  plan as planRoute,
} from '../../layout/routes';
import {
  Feature,
  getAddOnCodeByFeature,
  getProductId,
  OrderPreviewRequest,
  OrderStatus,
  PaymentMethodType,
  PlanInterval,
  SubscriptionSource,
} from '../../models';
import { useLaunchDarkly } from '../../launchdarkly';
import {
  PARTNER_ID_COOKIE_NAME,
  PLAN_SUBSCRIPTION_ID_QUERY_PARAM,
  SUPPORTED_FEATURES,
  TRUE_TESTCLOUD_ADDON_FEATURES,
} from '../../utils/Constants';
import { getRecurlySubUuIdFromPathParam, sendTrackingData } from '../../utils/utils';
import { withCartSnapshot } from '../../utils/analytics';
import {
  getTargetSelfServeSubscription,
  parseSubscriptionId,
} from '../../utils/subscriptionTarget';
import {
  buildPreviewProducts,
  isMonthlyToAnnualSwitchRequest,
  isMonthlyToAnnualSwitchPreview,
} from '../plan/component/true-platform/trueBundlePreview';
import {
  TRUE_MORE_TESTING_PRODUCT_FEATURES,
  TRUE_BUNDLE_PURCHASE_FEATURES,
} from '../plan/component/true-platform/trueBundlePlans';
import { getManualPurchaseProductCode } from '../plan/component/true-platform/trueBundleProductCode';
import { PlanStepNav } from '../plan/component/PlanStepNav';

const useStyles = makeStyles(theme => ({
  pageTitle: {
    fontFamily: "'Inter Tight', sans-serif",
    fontSize: 24,
    fontWeight: 700,
    letterSpacing: '-.01em',
    color: '#16141d',
  },
  pageSubtitle: {
    fontSize: 13.5,
    color: '#73718a',
    marginTop: theme.spacing(0.625),
  },
  continueButton: {
    width: '100%',
    height: 50,
    marginTop: theme.spacing(3.25),
    borderRadius: 12,
    backgroundColor: '#0f8461',
    color: '#ffffff',
    fontSize: 15.5,
    fontWeight: 700,
    textTransform: 'none',
    '&:hover': {
      backgroundColor: '#0b6b4e',
    },
    '&:disabled': {
      backgroundColor: '#b0b0b0',
      color: '#ffffff',
    },
  },
  continueHint: {
    margin: theme.spacing(1.75, 0, 0),
    textAlign: 'center',
    fontSize: 12,
    lineHeight: 1.55,
    color: '#9a98ad',
  },
}));

export const OrderConfirmationPage = () => {
  const { get, queryString, queryDictionary } = useQuery();
  const { replace, replaceQuery, redirect } = useNavigate();
  const { flags } = useLaunchDarkly();
  const intl = useIntl();
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const paymentInfoRef = useRef<any>(null);
  const checkoutDispatchedRef = useRef(false);
  const [loading, setLoading] = useState(false);
  const [checkoutStatus, setCheckoutStatus] = useState<'loading' | 'finished' | undefined>(undefined);
  const [cookies] = useCookies();

  const accountId = Number(get('accountId'));
  const featuresQuotaAndCycle = useGetQuotaAndCycleFromPathParams(SUPPORTED_FEATURES);

  // Store selectors
  const previewLoading = useSelector(fromOrder.selectPreviewLoading);
  const orderPreviewResponse = useSelector(fromOrder.selectOrderPreviewResponse);
  const orderResponse = useSelector(fromOrder.selectOrderResponse);
  const termsAndConditionsAccepted = useSelector(fromOrder.selectTermsAndConditionsAccepted);
  const couponName = useSelector(fromOrder.selectCouponName);
  const couponError = useSelector(fromOrder.selectCouponError);
  const couponInfo = useSelector(fromCoupon.selectCoupon);

  const selectedPaymentMethod = useSelector(fromPaymentMethod.selectSelectedPaymentMethod);
  const billingInfoLoading = useSelector(fromBillingInformation.selectLoading);
  const orderLoading = useSelector(fromOrder.selectLoading);
  const currentBillingInformation = useSelector(
    fromBillingInformation.selectOneByAccountId(accountId),
  );

  const cartSnapshot = usePlanCartSnapshot();
  const trackingCookies = useGetTrackingCookies();
  const checkoutError = useSelector(fromOrder.selectCheckoutError);
  const currentSubscriptions = useSelector(fromSubscription.selectByAccountId(accountId));
  const recurlySubscriptions = useSelector(
    fromSubscription.selectSubscriptionsBySource(SubscriptionSource.RECURLY),
  );
  const hasPaidSubscription = useSelector(fromSubscription.hasPaidSubscription(accountId));

  const legacyTrueTeamQuota = featuresQuotaAndCycle[Feature.TRUE_TEAM]?.quota ?? 0;
  const targetSubscriptionId = parseSubscriptionId(get(PLAN_SUBSCRIPTION_ID_QUERY_PARAM));

  const buildAddOns = (): Record<string, number> => {
    if (legacyTrueTeamQuota === 0) {
      return {};
    }

    const addOns: Record<string, number> = {};
    TRUE_TESTCLOUD_ADDON_FEATURES.forEach(feature => {
      const quota = featuresQuotaAndCycle[feature]?.quota ?? 0;
      const addOnCode = getAddOnCodeByFeature(feature);
      if (quota > 0 && addOnCode) {
        addOns[addOnCode.toUpperCase()] = quota;
      }
    });
    return addOns;
  };

  // Billing cycle
  const getRecurlySubscriptionUuid = (feature: Feature) => (
    getRecurlySubUuIdFromPathParam(get, feature)
    ?? getTargetSelfServeSubscription(
      recurlySubscriptions.filter(subscription => subscription.accountId === accountId),
      feature,
      targetSubscriptionId,
    )?.recurlySubUuId
  );

  const trueBundleProductRequests = TRUE_BUNDLE_PURCHASE_FEATURES
    .map(feature => {
      const quantity = featuresQuotaAndCycle[feature]?.quota ?? 0;
      const cycleParam = featuresQuotaAndCycle[feature]?.cycle;
      const cycle = cycleParam ?? PlanInterval.YEAR;
      // Only associate the existing subscription when the user actually engaged
      // this SKU (added seats or explicitly picked a billing cycle). Otherwise an
      // untouched monthly TA/TP would be resurrected here as a phantom
      // monthly -> annual switch — both in the summary and the real checkout.
      const isEngaged = quantity > 0 || cycleParam !== undefined;
      const recurlySubscriptionUuid = isEngaged
        ? getRecurlySubscriptionUuid(feature)
        : undefined;
      return {
        productCode: getManualPurchaseProductCode(feature, PlanInterval.fromString(cycle)),
        quantity,
        ...(recurlySubscriptionUuid ? { recurlySubscriptionUuid } : {}),
      };
    })
    .filter(product => product.quantity > 0
      || isMonthlyToAnnualSwitchRequest(product, currentSubscriptions));
  const moreTestingProductRequests = TRUE_MORE_TESTING_PRODUCT_FEATURES
    .map(feature => {
      const quantity = featuresQuotaAndCycle[feature]?.quota ?? 0;
      const cycleParam = featuresQuotaAndCycle[feature]?.cycle;
      const cycle = cycleParam ?? PlanInterval.YEAR;
      // Mirror the True bundle logic: only associate the existing subscription
      // when the user engaged this SKU (added seats or picked a cycle), so an
      // untouched monthly product is not resurrected as a phantom switch.
      const isEngaged = quantity > 0 || cycleParam !== undefined;
      const recurlySubscriptionUuid = isEngaged
        ? getRecurlySubscriptionUuid(feature)
        : undefined;
      return {
        productCode: `${getProductId(feature)}_${
          PlanInterval.getPlanIntervalCode(PlanInterval.fromString(cycle))
        }`,
        quantity,
        ...(recurlySubscriptionUuid ? { recurlySubscriptionUuid } : {}),
      };
    })
    .filter(product => product.quantity > 0
      || isMonthlyToAnnualSwitchRequest(product, currentSubscriptions));
  const legacyTrueTeamCycle = featuresQuotaAndCycle[Feature.TRUE_TEAM]?.cycle ?? PlanInterval.YEAR;
  const legacyRecurlySubscriptionUuid = getRecurlySubscriptionUuid(Feature.TRUE_TEAM);
  const previewProductsRequest = legacyTrueTeamQuota > 0
    ? [{
      productCode: `${getProductId(Feature.TRUE_TEAM)}_${
        PlanInterval.getPlanIntervalCode(PlanInterval.fromString(legacyTrueTeamCycle))
      }`,
      quantity: legacyTrueTeamQuota,
      ...(legacyRecurlySubscriptionUuid
        ? { recurlySubscriptionUuid: legacyRecurlySubscriptionUuid }
        : {}),
    }]
    : [
      ...trueBundleProductRequests,
      ...moreTestingProductRequests,
    ];
  const addOns = buildAddOns();
  const previewRequestProducts = previewProductsRequest.map((product, index) => ({
    ...product,
    ...(index === 0 && Object.keys(addOns).length > 0 ? { addOns } : {}),
  }));
  // Bank transfer is annual-only: any monthly item in the order hides the
  // option, mirroring the legacy checkout page.
  const hasMonthlyItem = previewRequestProducts.some(
    product => PlanInterval.getIntervalFromPlanCode(product.productCode) === PlanInterval.MONTH,
  );
  const productsPreview = buildPreviewProducts(
    previewRequestProducts,
    orderPreviewResponse,
    flags?.subscriptionPlanPrice,
  );
  const selectedProducts = productsPreview.filter(product => product.newQuantity > 0
    || isMonthlyToAnnualSwitchPreview(product, currentSubscriptions));
  const hasSelectedProducts = selectedProducts.length > 0;
  const buildPreviewRequest = (): OrderPreviewRequest => ({
    singleProductList: previewRequestProducts,
  });

  const buildCheckoutRequest = () => {
    const partnerId = cookies[PARTNER_ID_COOKIE_NAME];
    return {
      singleProductList: buildPreviewRequest().singleProductList,
      paymentMethod: selectedPaymentMethod,
      ...(couponName && { couponName }),
      ...(partnerId && { partnerId }),
    };
  };

  // Billing information is validated (and saved) on the Continue click itself,
  // so an empty or out-of-sync form must not pre-disable the button here. With
  // Stripe Checkout Session the card is collected on Stripe's hosted page, so no
  // saved card is required to enable checkout.
  // couponError blocks checkout: stale errors are cleared on mount, so a present
  // couponError means THIS page's preview dropped the applied coupon — proceeding
  // would silently charge full price against a discounted quote.
  const isCheckoutDisabled = loading || previewLoading
    || !termsAndConditionsAccepted
    || !hasSelectedProducts
    || !!couponError
    || (selectedPaymentMethod === PaymentMethodType.BANK_TRANSFER && hasMonthlyItem);

  // a message from the backend can be an i18n id (e.g. coupon.invalid.plan) or a
  // raw English sentence — render whichever form it is, fall back to error.unknown
  const renderServerMessage = (message?: string | null) => {
    if (message && intl.messages[message]) {
      return (
        <FormattedMessage
          id={message}
          values={{
            // same placeholder set the plan page's coupon renderer fills for
            // coupon.invalid.plan / coupon.min.license.required / coupon.min.amount.required
            quota: couponInfo?.rule?.minLicenses,
            amount: couponInfo?.rule?.minAmount,
            cycle: hasMonthlyItem ? 'annual' : 'monthly',
          }}
        />
      );
    }
    return message || intl.formatMessage({ id: 'error.unknown' });
  };

  const handleRemoveCoupon = () => {
    dispatch(fromOrder.clearCoupon());
    dispatch(fromOrder.doPreviewOrder(buildPreviewRequest()));
  };

  const handleSelectPaymentMethod = (paymentMethod: PaymentMethodType) => {
    dispatch(fromPaymentMethod.doChangeSelectedPaymentMethod(paymentMethod));
    if (PaymentMethodType.BANK_TRANSFER === paymentMethod) {
      dispatch(fromPaymentMethod.doUpdateCardInformationInvalid(false));
    }
  };

  const handleCheckout = () => {
    const doCheckout = async () => {
      setLoading(true);
      // Validate the billing form and persist it when it changed; abort the
      // checkout when validation or saving fails.
      const billingReady = await paymentInfoRef.current?.validateAndSaveBillingInformation?.();
      if (billingReady === false) {
        setLoading(false);
        return;
      }
      checkoutDispatchedRef.current = false;
      setCheckoutStatus('loading');
    };

    doCheckout().catch(() => {
      setLoading(false);
    });
  };

  const handleBackToConfiguration = () => {
    replace(
      planRoute.path,
      replaceQuery(parse(queryString()) as QueryDictionary),
    );
  };

  // A couponError arriving here with NO coupon attached is stale: it belongs to the
  // plan page's coupon-less fallback flow (the user already saw and accepted the
  // coupon-less quote there) — it must not block this page's checkout.
  useEffect(() => {
    if (couponError && !couponName) {
      dispatch(fromOrder.clearCoupon());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Effect: redirect to paymentStatus after successful checkout
  useEffect(() => {
    const redirectToPaymentStatusPage = () => {
      if (!orderResponse) return;
      replace(paymentStatusRoute.path, replaceQuery({
        ...queryDictionary(),
        accountId,
        orderId: orderResponse.id,
      }));
    };

    const performCheckout = async () => {
      await dispatch(fromOrder.doCheckout(buildCheckoutRequest()));
      setCheckoutStatus('finished');
    };

    if (checkoutStatus !== 'loading') {
      checkoutDispatchedRef.current = false;
    }

    // one user intent = at most one checkout POST: the billing-information entity
    // gets a new reference on every upsert (e.g. a slow mount-time GET landing after
    // the click), which re-runs this effect while the status is still 'loading'
    if (checkoutStatus === 'loading' && currentBillingInformation && !checkoutDispatchedRef.current) {
      checkoutDispatchedRef.current = true;
      performCheckout().finally(() => setLoading(false));
    }

    if (checkoutStatus === 'finished') {
      if (selectedPaymentMethod === PaymentMethodType.CREDIT_CARD) {
        const isPendingOrder = orderResponse?.status === OrderStatus.PENDING;
        const shouldRedirectToStripeCheckout = isPendingOrder
          && orderResponse?.stripeInvoiceUrl;

        if (shouldRedirectToStripeCheckout) {
          redirect(orderResponse!.stripeInvoiceUrl!);
        } else {
          redirectToPaymentStatusPage();
        }
      }

      if (selectedPaymentMethod === PaymentMethodType.BANK_TRANSFER) {
        // only a checkout that actually created an order may show the wire-transfer
        // instructions — a failed one falls through to the inline error alert
        if (!checkoutError && orderResponse) {
          replace(planRoute.path, replaceQuery({ accountId, showInstructionPopup: true }));
        }
      }
    }
  }, [checkoutStatus, currentBillingInformation]);

  const fetchInfo = async () => {
    if (!accountId) return;
    await Promise.all([
      dispatch(fromOrder.resetTermsAndConditionsAccepted()),
      dispatch(fromAccounts.doCheckPublicDomain(accountId)),
      dispatch(fromOrder.resetOrderPreviewRequest()),
      dispatch(fromSubscription.doFetchSubscriptions({ accountId })),
      dispatch(fromOrder.doFetchAllOrder(accountId)),
      dispatch(fromPaymentMethod.doGetDefaultPaymentMethod()),
      dispatch(fromAccounts.doCheckFirstThreeLicenses()),
    ]);
  };

  const sendCheckoutClickTracking = ({
    error,
    transactionId,
    invoiceNumbers,
  }: {
    error?: string;
    transactionId?: string | number;
    invoiceNumbers?: string[];
  } = {}) => {
    sendTrackingData(
      'checkout_page_all_action',
      accountId,
      undefined,
      withCartSnapshot({
        field: 'order_confirmation',
        object: 'button',
        action: 'checkout',
        plan_flow: legacyTrueTeamQuota > 0 ? 'true_team_legacy' : 'true_bundle',
        is_required: false,
        is_valid: !error,
        error,
        ...trackingCookies,
        ...(transactionId ? { transaction_id: transactionId } : {}),
        ...(invoiceNumbers && invoiceNumbers.length > 0 ? { invoice_numbers: invoiceNumbers } : {}),
      }, cartSnapshot),
    );
  };

  useEffect(() => {
    if (checkoutStatus !== 'finished') {
      // Action doCheckout - not dispatch yet
      return;
    }
    const hasCheckoutResult = !!checkoutError || !!orderResponse;
    if (!hasCheckoutResult) {
      return;
    }

    const isSuccessCheckout = !!orderResponse;
    if (isSuccessCheckout) {
      const invoiceNumbers = orderResponse?.singleProductInvoices;
      const transactionId = orderResponse?.id;

      sendCheckoutClickTracking({
        transactionId,
        invoiceNumbers,
      });
    } else {
      sendCheckoutClickTracking({
        error: checkoutError?.message,
      });
    }
  }, [checkoutError, orderResponse, checkoutStatus]);

  // Fetch order preview whenever quotas/cycle change; keep a coupon applied
  // on the plan page attached to this preview as well.
  useEffect(() => {
    if (!accountId || loading || billingInfoLoading || orderLoading) return;
    dispatch(fromOrder.doPreviewOrder({
      ...buildPreviewRequest(),
      ...(couponName && { couponName }),
    }));
  }, [currentBillingInformation]);

  const isBankTransfer = selectedPaymentMethod === PaymentMethodType.BANK_TRANSFER;
  const continueLabelId = isBankTransfer
    ? 'page.order.confirmation.continue.bank'
    : 'page.order.confirmation.continue.payment';

  return (
    <Page
      id="order-confirmation"
      headerId="page.order.confirmation.header"
      fetchData={fetchInfo}
      background="#fafafd"
      hideHeader
    >
      <PlanStepNav currentStep={2} onBackToPlan={handleBackToConfiguration} />
      <Box display="flex" justifyContent="center">
        <Box width="100%" maxWidth={680} pt={5} pb={9}>
          <Typography className={classes.pageTitle}>
            <FormattedMessage id="page.order.confirmation.v2.title" />
          </Typography>
          <Typography className={classes.pageSubtitle}>
            <FormattedMessage id="page.order.confirmation.v2.subtitle" />
          </Typography>

          <PaymentInformationSection
            ref={paymentInfoRef}
            accountId={accountId}
            enableSave
            availableRenewal
            showCardInformation={false}
            hasMonthlyItem={hasMonthlyItem}
            hasPaidSubscription={hasPaidSubscription}
            handleSelectPaymentMethod={handleSelectPaymentMethod}
            redesign
          />

          <Box mt={3}>
            <TermAndConditionCheck redesign />
          </Box>

          {couponError && (
            <Alert
              severity="error"
              sx={{ mt: 3 }}
              action={(
                <Button color="inherit" size="small" onClick={handleRemoveCoupon}>
                  <FormattedMessage id="page.order.confirmation.coupon.remove" />
                </Button>
              )}
            >
              {renderServerMessage(couponError)}
            </Alert>
          )}
          {checkoutStatus === 'finished' && checkoutError && (
            <Alert severity="error" sx={{ mt: 3 }}>
              {renderServerMessage(checkoutError.message)}
            </Alert>
          )}

          <Button
            id="order-confirmation-continue"
            variant="contained"
            className={classes.continueButton}
            onClick={handleCheckout}
            disabled={isCheckoutDisabled}
          >
            <FormattedMessage id={continueLabelId} />
          </Button>
          <Typography className={classes.continueHint}>
            <FormattedMessage
              id={isBankTransfer
                ? 'page.order.confirmation.hint.bank'
                : 'page.order.confirmation.hint.card'}
            />
          </Typography>
        </Box>
      </Box>
      {(loading || previewLoading) && <LoadingProgress fullscreen />}
    </Page>
  );
};
